import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { I18n } from 'aws-amplify'

import { Container, Row, Col, Breadcrumb, Spinner } from 'react-bootstrap'
import { Header, Footer, SidebarOffcanvas, NavItems } from './../../shared/components/Panels'

import { getSimpleUser } from './api/User'

import { ConstantEquals, USER } from './../../shared/constants/index'
import { ValidMessage, ValidUsername } from './Validate'

import { UserUpdatedAtContext } from './context/UserUpdatedAt'
import { LicenceLimitContext } from './context/LicenceLimit'
import { LicenceNumContext } from './context/LicenceNum'

import { Username } from './Username'
import { UpdatedAt } from './UpdatedAt'
import { Active } from './Active'
import { Corp } from './Corp'
import { Delete as UserDelete } from './Delete'
import { Detail as UserDetail } from './Detail'
import { LicenceLimit } from './components/LicenceLimit/LicenceLimit'
import { Licence } from './components/Licence/Licence'

/**
 * UserページのReact Hook版
 * - ユーザ詳細
 */
const UserView = props => {
  const udRef = useRef()

  // Offcanvas（モバイル）表示
  const [showSidebar, setShowSidebar] = useState(false);

  // 更新中
  const [userLoad, setUserLoad] = useState(false)

  // ユーザ詳細エラー
  const [userErrors, setUserErrors] = useState({})
  // ユーザ名
  const [username, setUsername] = useState(null)
  // アクティブ
  const [userActive, setUserActive] = useState(false)
  // 拡張機能
  const [userCorp, setUserCorp] = useState(null)

  // 更新日
  const [userUpdatedAt, setUserUpdatedAt] = useState(null)
  // ライセンス上限
  const [currentLicenceLimit, setCurrentLicenceLimit] = useState({
    use: 0
  })
  // ライセンスの数
  // - ライセンス上限を変更する際、この数を下回らないようにする
  const [currentLicence, setCurrentLicence] = useState({
    use: 0 
  })

  const licenceLimitValue = React.useMemo(() => currentLicenceLimit, [currentLicenceLimit]);
  const licenceNumValue = React.useMemo(() => currentLicence, [currentLicence]);
  const updatedAtValue = React.useMemo(() => userUpdatedAt, [userUpdatedAt]);

  // 初回マウント時
  useEffect(() => {
    // Stateのメモリリーク対策
    let isMounted = true;
    // 取得
    // Note: 3つここで取得するか後で取得するか悩みどころさんではある
    (async() => {

      try{

        const fetchUserDetail = await getSimpleUser(props.match.params.id)

        // 取得した情報をparseする
        const parseData = parse(fetchUserDetail?.data?.getUser)

        if(!parseData.done){
          if (isMounted){
            setUserLoad(false)
            setUsername(null)
            setUserCorp(null)
          }
          return;
        }

        // 描画
        if (isMounted){
          setUsername(parseData.username)
          setUserActive(parseData.active)
          setUserUpdatedAt(parseData.updatedAt)
          setUserCorp(parseData.corp)
          setUserErrors(parseData.errors)
          setUserLoad(true)
        }
  
      }
      catch(err){
        //console.error(err);
        if (isMounted){
          setUserErrors(prev => ({...prev, fetch: I18n.get('error_fetch_user') || 'ユーザ情報の取得に失敗しました'}));
          setUserLoad(false);
        }
      }

    })()
    return () => { 
      // マウントされていない場合はフラグをクリーンにする（setStateさせない）
      isMounted = false 
    };
  }, []);

  // Note: 確認用
  useEffect(() => {
    // console.log(currentLicenceLimit)
  }, [currentLicenceLimit])

  // Note: 確認用
  useEffect(() => {
    // console.log(currentLicence)
  }, [currentLicence])

  // パース：取得したユーザ情報
  const parse = (argUser) => {

    const parseData = {
      done: false,
      errors: {},
      updatedAt: "",
      username: null,
      active: true,
      corp: null
    };

    // No Data / null / 空オブジェクトの場合
    if (!argUser || Object.keys(argUser).length === 0) {
      parseData.done = true;
      return parseData;
    }

    Object.entries(argUser).forEach(([key, value]) => {
      // ユーザ名
      if(ConstantEquals(key, USER.LABEL.USERNAME)){
        const vUsername = ValidUsername(value)
        if (!ConstantEquals(vUsername, USER.VALIDATE.CLEAR) ) {
          parseData.errors[USER.LABEL.USERNAME] = ValidMessage(vUsername);
        }else{
          parseData.username = argUser.username;
        }
      }

      // アクティブ
      if(ConstantEquals(key, USER.LABEL.ACTIVE)){
        parseData.active = argUser.active;
      }

      // 拡張機能
      if(ConstantEquals(key, USER.LABEL.CORP)){
        parseData.corp = argUser.corp;
      }

      // 更新日
      if(ConstantEquals(key, USER.LABEL.UPDATED_AT)){
        parseData.updatedAt = argUser.updatedAt;
      }

    })

    parseData.done = true;
    return parseData;
  }

  /**
   * Activeバッジの描画
   * @param {*} arg boolean
   */
  const applyUserActive = (arg) => {
    setUserActive(arg)
  }

  /**
   * 更新日の描画
   * @param {*} arg YYYY-MM-DDTHH:mm:00Z
   */
  const applyUpdatedAt = (arg) => {
    // Todo: validateするべき

    setUserUpdatedAt(arg)
  }

  // ユーザ名更新時に親も更新
  const applyUsername = (newName) => {
    setUsername(newName)
  }

  /**
   * ライセンス上限数の更新
   * 
   * @param {*} argObj 
   */
  const applyLicenceLimitNum = (argObj) => {
    setCurrentLicenceLimit(argObj)
  }

  /**
   * ライセンス数の更新
   * 
   * @param {*} argObj 
   */
   const applyLicenceNum = (argObj) => {
    setCurrentLicence(argObj)
  }

  return (
    <>
    <Header onOpenSidebar={() => setShowSidebar(true)} />
    <SidebarOffcanvas
      show={showSidebar}
      onHide={() => setShowSidebar(false)}
    />

    <Container fluid className="p-0">
      <Row className="g-0">
          {/* 左 */}
          <Col lg={2} className="p-0 d-none d-lg-block sidebar-col">
            <NavItems />
          </Col>

          {/* 右 */}
          <Col lg={10} className="p-3 main-content">
          <Container>
            <Row>
              <Col xs={12} md={12} className="mt-1">
                <Breadcrumb >
                  <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/users' }}>
                    {I18n.get('title_user_list')}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>
                    {I18n.get('title_user_detail')}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
            </Row>
          </Container>

          {Object.keys(userErrors).length > 0 && (
            <Container>
              <Row><Col className="mt-1">
                <div className="alert alert-warning p-2">
                  {Object.values(userErrors).join(' / ')}
                </div>
              </Col></Row>
            </Container>
          )}

          {
            !userLoad ? (
              <Container>
                <Row>
                  <Col xs={12} md={12} className="mt-1 text-center">
                    <Spinner
                      as="span"
                      animation="border"
                      role="status"
                      aria-hidden="true" />
                  </Col>
                </Row>
              </Container>
            ) : (
              <>

                <UserUpdatedAtContext.Provider value={updatedAtValue}>
                  <Container className="main-content">
                    <Username 
                      userId={props.match.params.id}
                      username={username} 
                      doneUpdate={applyUpdatedAt}
                      onChangeUsername={applyUsername}
                    />
                    <Row className="mt-1">
                      <Col xs={12} md={6} >
                        {
                          userActive ? (
                            <span className="badge bg-success">{I18n.get('general_active')}</span>
                          ) : (
                            <span className="badge bg-danger">{I18n.get('general_inactive')}</span>
                          )
                        }{' '}
                        <UpdatedAt updatedAt={userUpdatedAt} />
                      </Col>
                      <Col xs={12} md={6} >
                        <div className="text-end" >
                          <Active 
                            userId={props.match.params.id}
                            active={userActive}
                            doneUpdateActive={applyUserActive}
                            doneUpdatedAt={applyUpdatedAt} />
                          {` `}
                          <UserDelete 
                            userId={props.match.params.id} />
                        </div>
                      </Col>
                    </Row>
                    <hr />
                    <Row>
                      <Col xs={12} md={8}className="mt-1">
                        <UserDetail
                          userId={props.match.params.id}
                          doneUpdate={applyUpdatedAt}
                        />
                      </Col>

                      <Col xs={12} md={4}>
                        <Row>
                          <Col xs={12} md={12} className="mt-1">
                            <LicenceNumContext.Provider value={licenceNumValue}>
                              <LicenceLimit 
                                userId={props.match.params.id}
                                applyLicenceLimitNum={applyLicenceLimitNum}
                                doneUpdatedAt={applyUpdatedAt} />
                            </LicenceNumContext.Provider>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12} md={12} className="mt-3">
                            <Corp 
                              corp={userCorp} />
                          </Col>
                        </Row>
                      </Col> 

                    </Row>
                    <hr />

                    <LicenceLimitContext.Provider value={licenceLimitValue}>
                      <Licence 
                        userId={props.match.params.id}
                        applyLicenceNum={applyLicenceNum}
                        doneUpdatedAt={applyUpdatedAt} />
                    </LicenceLimitContext.Provider>
                  </Container>
                </UserUpdatedAtContext.Provider>
              </>
            )
          }
        </Col>
      </Row>
    </Container>

    <Footer />
    </>
  );
}

export default UserView